import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { restaurantInfo, translations } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { galleryUi } from '../data/galleryData';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.about, href: '#chi-siamo' },
    { name: t.nav.menu, href: '#menu' },
    { name: galleryUi.nav[language], href: '#galleria' },
    { name: t.nav.hours, href: '#orari' },
    { name: t.nav.contact, href: '#contatti' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#3d2617]/95 backdrop-blur-md shadow-2xl py-2'
          : 'bg-transparent py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group">
            <div className={`transition-all duration-500 ${isScrolled ? 'opacity-100' : 'opacity-100'}`}>
              <h1 className={`font-serif text-xl font-bold whitespace-nowrap transition-colors ${
                isScrolled ? 'text-[#c9a227]' : 'text-[#e8d48b]'
              }`}>Mezzaluna</h1>
              <p className={`text-xs uppercase tracking-widest transition-colors ${
                isScrolled ? 'text-[#e8d48b]' : 'text-white/80'
              }`}>Ristorante</p>
            </div>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`relative font-medium transition-colors py-2 group ${
                  isScrolled ? 'text-white/90 hover:text-[#c9a227]' : 'text-white hover:text-[#e8d48b]'
                }`}
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c9a227] transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </nav>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSelector isScrolled={isScrolled} />

            {/* CTA Button */}
            <a
              href="#contatti"
              className="flex items-center gap-2 bg-[#c9a227] text-white px-6 py-2.5 rounded-full font-semibold hover:bg-[#8b7119] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Phone size={16} />
              {t.nav.book}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex lg:hidden items-center gap-3">
            <LanguageSelector compact />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 transition-colors ${
                isScrolled ? 'text-white hover:text-[#c9a227]' : 'text-white hover:text-[#c9a227]'
              }`}
            >
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden absolute top-full left-0 right-0 bg-[#3d2617] shadow-2xl transition-all duration-500 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav className="container mx-auto px-4 py-6 flex flex-col gap-4">
          {navLinks.map((link, index) => (
            <a
              key={link.name}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-white/90 font-medium hover:text-[#c9a227] transition-colors py-2 border-b border-white/10 animate-fadeInLeft"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {link.name}
            </a>
          ))}
          <a
            href="#contatti"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 bg-[#c9a227] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#8b7119] transition-colors mt-4"
          >
            <Phone size={16} />
            {t.nav.book}
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
